import { Box, Flex, Text } from '@chakra-ui/layout';
import { Progress } from '@chakra-ui/react';
import { StudentEnrolmentHealth, type StudentPlacementStatus } from '@duke-hq/svc-api-db';
import { calculateEnrolmentMetrics } from '@duke-hq/svc-api/lib/enrolment/calculateEnrolmentMetrics';
import { EnrolmentHealthBadge } from '@duke-hq/ui/components/EnrolmentHealthBadge/EnrolmentHealthBadge';
import { PlacementStatusBadge } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

export interface EnrolmentProgressLabels {
  progress: string;
  semester: string;
  placement: string;
}

const defaultLabels: EnrolmentProgressLabels = {
  progress: 'Progress',
  semester: 'Semester',
  placement: 'Placement',
};

export type EnrolmentProgressProps = {
  enrolmentMetrics: Pick<
    ReturnType<typeof calculateEnrolmentMetrics>,
    'currentSemester' | 'expectedProgress' | 'semesterProgress'
  >;
  enrolment: {
    progress_health: string | null;
    health: StudentEnrolmentHealth;
    workPlacementStatus?: StudentPlacementStatus | null;
    workPlacementOnHold?: boolean;
  };
  isSkillSet: boolean;
  labels?: Partial<EnrolmentProgressLabels>;
};

function EnrolmentProgress({
  enrolmentMetrics,
  enrolment,
  isSkillSet,
  labels,
}: EnrolmentProgressProps): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const { currentSemester, expectedProgress, semesterProgress } = enrolmentMetrics;
  const { progress_health, health, workPlacementStatus, workPlacementOnHold } = enrolment;

  const isAtRiskOrOverdue =
    progress_health !== null ? ['At risk', 'Overdue'].includes(progress_health) : false;

  return (
    <Box
      gap="5px"
      alignItems="center"
      backgroundColor="bg.surface"
      rounded={6}
      borderWidth={1}
      borderColor="border.default"
      p={3}
      flex="1"
      w="full"
    >
      <Flex justifyContent="space-between" gap={3} alignItems="center" flexWrap="wrap">
        <Flex alignItems="center" gap={2}>
          <Text fontWeight="medium" whiteSpace="nowrap">
            {isSkillSet ? (
              l.progress
            ) : (
              <>
                {l.semester} {currentSemester === 0 ? 1 : currentSemester} {l.progress}
              </>
            )}
          </Text>
          <EnrolmentHealthBadge health={health} />
        </Flex>
        {workPlacementStatus != null && (
          <Flex alignItems="center" gap={2}>
            <Text fontWeight="medium">{l.placement}</Text>
            <PlacementStatusBadge status={workPlacementStatus} onHold={workPlacementOnHold} />
          </Flex>
        )}
      </Flex>

      <Box
        position="relative"
        rounded={12}
        overflow="hidden"
        height={pxToRem(44)}
        width="100%"
        mt={3}
      >
        <Progress
          value={currentSemester === 0 ? 0 : semesterProgress}
          rounded={12}
          height={pxToRem(44)}
          width="100%"
          sx={{
            '& > div': {
              background: 'blue.200',
              rounded: 0,
            },
          }}
          borderColor="info"
          borderWidth={1}
          aria-label={`Current progress ${currentSemester === 0 ? 0 : semesterProgress}%`}
        />

        <Flex
          borderRight="1px"
          borderColor={isAtRiskOrOverdue ? 'danger' : 'info'}
          borderStyle="dashed"
          position="absolute"
          top={0}
          height={pxToRem(44)}
          width={`${expectedProgress.toFixed(1)}%`}
          left={0}
          alignItems="center"
          aria-label={`Expected progress ${expectedProgress.toFixed(1)}%`}
        >
          <Text size="sm" fontWeight="semibold" ml={4}>
            {currentSemester === 0 ? 0 : semesterProgress}%
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}

export default EnrolmentProgress;
